/**
 * Parse a date string as Eastern Time (America/New_York) and return
 * the corresponding UTC epoch in milliseconds.
 *
 * CMS/Monarch countdown dates like "December 2, 2024 23:59:59" are intended
 * as Eastern Time, but `new Date(...)` interprets them in the user's local
 * timezone. This helper corrects for that using the Intl API to find the
 * Eastern UTC offset and shifting to the correct instant.
 *
 * Handles both EST (-05:00) and EDT (-04:00) automatically.
 */
export const parseAsEastern = (dateString: string): number => {
  const local = new Date(dateString);

  if (isNaN(local.getTime())) {
    return NaN;
  }

  const year = local.getFullYear();
  const month = local.getMonth();
  const day = local.getDate();
  const hours = local.getHours();
  const minutes = local.getMinutes();
  const seconds = local.getSeconds();
  const ms = local.getMilliseconds();

  // Place the intended calendar values at UTC so we have a
  // timezone-neutral reference point.
  const utcGuess = Date.UTC(year, month, day, hours, minutes, seconds, ms);

  // Ask Intl what Eastern Time wall-clock values correspond to utcGuess.
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hourCycle: "h23",
  });

  const parts = formatter.formatToParts(new Date(utcGuess));
  const get = (type: Intl.DateTimeFormatPartTypes) => parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);

  // The difference between the UTC input and the Eastern representation
  // (also expressed in UTC) gives us the Eastern UTC offset at that moment.
  const easternWallAsUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second")
  );
  const easternOffsetMs = easternWallAsUtc - utcGuess;

  return utcGuess - easternOffsetMs;
};

export default parseAsEastern;
